import React, { useState, useEffect, useMemo } from "react";
import { Scroll, MapPin, Book, X } from "lucide-react";
import CourseInfoFilter from "./FilterCard/CourseInfoFilter";
import LocationFilter from "./FilterCard/LocationFilter";
import FinancialTimingFilter from "./FilterCard/FinancialTimingFilter";
import { initialState } from "../../Data/courseFilterData";
import { useCourses } from "../../context/CourseContext";

// Button Configuration
const filterButtons = [
  {
    id: "courseInfo",
    label: "Course Info",
    labelPhone: "Course",
    icon: Scroll,
  },
  {
    id: "financials",
    label: "Financials & Timing",
    labelPhone: "Financials",
    icon: Book,
  },
  {
    id: "location",
    label: "Location & Environment",
    labelPhone: "Location",
    icon: MapPin,
  },
];

interface FilterProps {
  onFilterChange?: (filterState: typeof initialState) => void;
  onClose?: () => void;
  preselectedFieldOfStudy?: string;
}

export default function Filter({
  onFilterChange,
  onClose,
  preselectedFieldOfStudy,
}: FilterProps) {
  const [activeButton, setActiveButton] = useState("courseInfo");
  const [filterState, setFilterState] = useState(initialState);

  useEffect(() => {
    if (preselectedFieldOfStudy) {
      setFilterState((prev) => ({
        ...prev,
        courseInfo: {
          ...prev.courseInfo,
          selectedFieldOfStudy: preselectedFieldOfStudy,
        },
      }));
    }
  }, [preselectedFieldOfStudy]);

  const { metadata } = useCourses();

  // Notify parent component when filter state changes
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filterState);
    }
  }, [filterState, onFilterChange]);

  const handleClearFilter = () => {
    setFilterState(initialState);
  };

  // Centralized options for all filters
  // Course Info Options
  // Centralized options for all filters provided by metadata
  const fieldOfStudyOptions = useMemo(() => {
    if (!metadata) return [];
    const fields = metadata.fieldOfStudy || [];
    const fosOptions = fields
      .map((item: any) => {
        if (typeof item === "string") {
          return {
            value: item.toLowerCase().replace(/\s+/g, "-"),
            label: item,
          };
        }
        return {
          value: item.slug || item.value || "",
          label: item.fieldOfStudy || item.label || "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
    return [{ value: "", label: "Any" }, ...fosOptions];
  }, [metadata]);

  const levelOptions = useMemo(() => {
    if (!metadata) return [];
    const levels = metadata.level || [];
    const lvlOptions = levels
      .map((item: any) => {
        if (typeof item === "string") {
          return { value: item, label: item };
        }
        return {
          value: item.value || item.slug || "",
          label: item.label || item.level || "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
    return [{ value: "", label: "Any" }, ...lvlOptions];
  }, [metadata]);

  const intakeMonthOptions = useMemo(() => {
    if (!metadata) return [];
    const monthOrder = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEPT",
      "OCT",
      "NOV",
      "DEC",
    ];
    const monthLabelMap: { [key: string]: string } = {
      JAN: "January",
      FEB: "February",
      MAR: "March",
      APR: "April",
      MAY: "May",
      JUN: "June",
      JUL: "July",
      AUG: "August",
      SEPT: "September",
      OCT: "October",
      NOV: "November",
      DEC: "December",
    };

    const intakeData = metadata.intake || metadata.intakeMonth || [];
    const intakeOpts = intakeData
      .map((month: string) => ({
        value: month,
        label: monthLabelMap[month] || month,
      }))
      .sort((a: any, b: any) => {
        const aIdx = monthOrder.indexOf(a.value.toUpperCase());
        const bIdx = monthOrder.indexOf(b.value.toUpperCase());
        if (aIdx === -1 && bIdx === -1) return a.label.localeCompare(b.label);
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      });
    return [{ value: "", label: "Any" }, ...intakeOpts];
  }, [metadata]);

  const overseasTransferOptions = useMemo(() => {
    if (!metadata) return [];
    const transfers = metadata.overseasTransfer || [];
    const transferOpts = transfers
      .map((item: any) => {
        if (typeof item === "string") {
          return {
            value: item.toLowerCase().replace(/\s+/g, "-"),
            label: item,
          };
        }
        return {
          value: item.slug || item.value || "",
          label: item.label || item.overseasTransfer || "",
        };
      })
      .sort((a: any, b: any) => {
        if (a.value === "none") return -1;
        if (b.value === "none") return 1;
        return a.label.localeCompare(b.label);
      });
    return [{ value: "", label: "Any" }, ...transferOpts];
  }, [metadata]);

  const partTimeOptions = useMemo(() => {
    if (!metadata) return [];
    const partTimeData = metadata.partTimeOption || [];
    const ptOptions = partTimeData
      .map((item: any) => {
        if (typeof item === "string") {
          return { value: item, label: item };
        }
        return {
          value: item.value || item.slug || "",
          label: item.label || item.partTimeOption || "",
        };
      })
      .sort((a: any, b: any) => {
        const orderMap = { Yes: 1, No: 2 };
        const aOrder = orderMap[a.value as keyof typeof orderMap] || 3;
        const bOrder = orderMap[b.value as keyof typeof orderMap] || 3;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.label.localeCompare(b.label);
      });
    return [{ value: "", label: "Any" }, ...ptOptions];
  }, [metadata]);

  const countryOptions = useMemo(() => {
    if (!metadata) return [];
    const countryData = metadata.country || [];
    const cntryOptions = countryData
      .map((item: any) => {
        if (typeof item === "string") {
          return {
            value: item.toLowerCase().replace(/\s+/g, "-"),
            label: item,
          };
        }
        return {
          value: item.slug || item.value || "",
          label: item.country || item.label || "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
    return [{ value: "", label: "Any" }, ...cntryOptions];
  }, [metadata]);

  const durationAbroadOptions = useMemo(() => {
    if (!metadata) return [];
    const abroadData = metadata.abroadDuration || [];
    const abroadOpts = abroadData
      .map((item: any) => {
        if (typeof item === "string") {
          return { value: item, label: item };
        }
        return {
          value: item.value || item.slug || "",
          label: item.label || item.abroadDuration || "",
        };
      })
      .sort(
        (a: any, b: any) => (parseInt(a.value) || 0) - (parseInt(b.value) || 0),
      );
    return [{ value: "", label: "Any" }, ...abroadOpts];
  }, [metadata]);

  const majorOptions = useMemo(() => {
    if (
      !filterState.courseInfo.selectedFieldOfStudy ||
      !metadata ||
      fieldOfStudyOptions.length === 0
    ) {
      return [{ value: "", label: "Any" }];
    }

    // Find the label for the selected fieldOfStudy slug
    const selectedFosOption = fieldOfStudyOptions.find(
      (opt) => opt.value === filterState.courseInfo.selectedFieldOfStudy,
    );

    if (!selectedFosOption) return [{ value: "", label: "Any" }];

    const fosLabel = selectedFosOption.label;
    const majors = metadata[fosLabel] || [];

    const options = (majors as any[])
      .map((item: any) => {
        if (typeof item === "string") {
          return {
            value: item.toLowerCase().replace(/\s+/g, "-"),
            label: item,
          };
        }
        return {
          value: item.slug || item.value || "",
          label: item.major || item.label || "",
        };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));

    return [{ value: "", label: "Any" }, ...options];
  }, [
    metadata,
    fieldOfStudyOptions,
    filterState.courseInfo.selectedFieldOfStudy,
  ]);

  // Financial Timing Options
  const maxFee = useMemo(() => {
    return metadata?.tuitionFee;
  }, [metadata]);

  // paymentSchemesOptions logic remains commented out per previous request
  const paymentSchemesOptions = [{ value: "", label: "Any" }];

  const maxDuration = useMemo(() => {
    return metadata?.duration;
  }, [metadata]);

  // State Options
  const stateOptions = useMemo(() => {
    if (
      !filterState.location.selectedCountry ||
      !metadata ||
      countryOptions.length === 0
    ) {
      return [{ value: "", label: "Any" }];
    }

    // Find the label for the selected country slug
    const selectedCountryOption = countryOptions.find(
      (opt) => opt.value === filterState.location.selectedCountry,
    );

    if (!selectedCountryOption) return [{ value: "", label: "Any" }];

    const countryLabel = selectedCountryOption.label;
    const states = metadata[countryLabel] || [];

    const options = (states as any[])
      .map((state: any) => {
        if (typeof state === "string") {
          return {
            value: state.toLowerCase().replace(/\s+/g, "-"),
            label: state,
          };
        }
        return {
          value: state.slug || state.value || "",
          label: state.state || state.label || "",
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return [{ value: "", label: "Any" }, ...options];
  }, [metadata, countryOptions, filterState.location.selectedCountry]);

  const universityOptions = useMemo(() => {
    const list = metadata?.universities || [];
    const options = list
      .map((u: any) => ({
        value: u.slug || "",
        label: u.universityName || "",
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
    return [{ value: "", label: "Any" }, ...options];
  }, [metadata]);

  // Pass options as props to children
  const componentMap = {
    courseInfo: (
      <CourseInfoFilter
        selectedFieldOfStudy={filterState.courseInfo.selectedFieldOfStudy}
        setSelectedFieldOfStudy={(value) =>
          setFilterState((prev) => ({
            ...prev,
            courseInfo: { ...prev.courseInfo, selectedFieldOfStudy: value },
          }))
        }
        selectedMajor={filterState.courseInfo.selectedMajor}
        setSelectedMajor={(value) =>
          setFilterState((prev) => ({
            ...prev,
            courseInfo: { ...prev.courseInfo, selectedMajor: value },
          }))
        }
        selectedLevel={filterState.courseInfo.selectedLevel}
        setSelectedLevel={(value) =>
          setFilterState((prev) => ({
            ...prev,
            courseInfo: { ...prev.courseInfo, selectedLevel: value },
          }))
        }
        fieldOfStudyOptions={fieldOfStudyOptions}
        majorOptions={majorOptions}
        levelOptions={levelOptions}
      />
    ),
    financials: (
      <FinancialTimingFilter
        feeBudget={filterState.financials.feeBudget}
        setFeeBudget={(value) =>
          setFilterState((prev) => ({
            ...prev,
            financials: { ...prev.financials, feeBudget: value },
          }))
        }
        fullTimeDuration={filterState.financials.fullTimeDuration}
        setFullTimeDuration={(value) =>
          setFilterState((prev) => ({
            ...prev,
            financials: { ...prev.financials, fullTimeDuration: value },
          }))
        }
        paymentSchemes={filterState.financials.paymentSchemes}
        setPaymentSchemes={(value) =>
          setFilterState((prev) => ({
            ...prev,
            financials: { ...prev.financials, paymentSchemes: value },
          }))
        }
        paymentSchemesOptions={paymentSchemesOptions}
        intakeMonth={filterState.financials.intakeMonth}
        setIntakeMonth={(value) =>
          setFilterState((prev) => ({
            ...prev,
            financials: { ...prev.financials, intakeMonth: value },
          }))
        }
        intakeMonthOptions={intakeMonthOptions}
        partTimeOption={filterState.financials.partTimeOption}
        setPartTimeOption={(value) =>
          setFilterState((prev) => ({
            ...prev,
            financials: { ...prev.financials, partTimeOption: value },
          }))
        }
        partTimeOptions={partTimeOptions}
        durationAbroad={filterState.financials.durationAbroad}
        setDurationAbroad={(value) =>
          setFilterState((prev) => ({
            ...prev,
            financials: { ...prev.financials, durationAbroad: value },
          }))
        }
        durationAbroadOptions={durationAbroadOptions}
        maxFee={maxFee}
        maxDuration={maxDuration}
      />
    ),
    location: (
      <LocationFilter
        selectedCountry={filterState.location.selectedCountry}
        setSelectedCountry={(value) =>
          setFilterState((prev) => ({
            ...prev,
            location: { ...prev.location, selectedCountry: value },
          }))
        }
        selectedState={filterState.location.selectedState}
        setSelectedState={(value) =>
          setFilterState((prev) => ({
            ...prev,
            location: { ...prev.location, selectedState: value },
          }))
        }
        selectedInstitution={filterState.location.selectedInstitution}
        setSelectedInstitution={(value) =>
          setFilterState((prev) => ({
            ...prev,
            location: { ...prev.location, selectedInstitution: value },
          }))
        }
        selectedEnvironment={filterState.location.selectedEnvironment}
        setSelectedEnvironment={(value) =>
          setFilterState((prev) => ({
            ...prev,
            location: { ...prev.location, selectedEnvironment: value },
          }))
        }
        selectedPublicTransport={filterState.location.selectedPublicTransport}
        setSelectedPublicTransport={(value) =>
          setFilterState((prev) => ({
            ...prev,
            location: { ...prev.location, selectedPublicTransport: value },
          }))
        }
        selectedOverseasTransfer={filterState.location.selectedOverseasTransfer}
        setSelectedOverseasTransfer={(value) =>
          setFilterState((prev) => ({
            ...prev,
            location: { ...prev.location, selectedOverseasTransfer: value },
          }))
        }
        universityOptions={universityOptions}
        countryOptions={countryOptions}
        stateOptions={stateOptions}
        overseasTransferOptions={overseasTransferOptions}
      />
    ),
  };

  return (
    <div className="w-full">
      <div className="hidden lg:flex flex-col gap-4 border-b-2 pb-6 border-gray-200 w-full">
        <div className="flex justify-between p-2">
          <div className="flex gap-8">
            {filterButtons.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`flex items-center gap-2 text-lg cursor-pointer ${
                  activeButton === id ? "text-blue-600 font-semibold" : ""
                }`}
                onClick={() => setActiveButton(id)}
              >
                <Icon className="w-8 h-8" />
                {label}
              </button>
            ))}
          </div>
          <button
            className="flex items-center text-red-500 cursor-pointer"
            onClick={handleClearFilter}
          >
            <X />
            Clear Filter
          </button>
        </div>
        {componentMap[activeButton as keyof typeof componentMap]}
      </div>

      {/* mobile view */}
      <div className="lg:hidden flex flex-col gap-2 border-b-2 pb-6 border-gray-200 w-full">
        <div className="flex items-center justify-between px-4 py-2 w-full">
          <h2 className="text-lg font-semibold">Filters</h2>
          <button aria-label="Close filters" className="" onClick={onClose}>
            <X />
          </button>
        </div>
        <div className="flex flex-col gap-2 px-4 w-full">
          <div className="flex gap-20 justify-center">
            {filterButtons.map(({ id, labelPhone, icon: Icon }) => (
              <button
                key={id}
                className={`flex flex-col items-center gap-2 text-xs cursor-pointer ${
                  activeButton === id ? "text-blue-600 font-semibold" : ""
                }`}
                onClick={() => setActiveButton(id)}
              >
                <Icon className="w-5 h-5" />
                {labelPhone}
              </button>
            ))}
          </div>
          <button
            className="flex items-center justify-end text-red-500 cursor-pointer"
            onClick={handleClearFilter}
          >
            Clear Filter
          </button>
        </div>
        <div className="min-h-[380px] pb-2">
          {componentMap[activeButton as keyof typeof componentMap]}
        </div>
        <div className="flex items-center justify-center">
          <button
            onClick={onClose}
            className="text-white rounded bg-blue-500 p-2 w-28"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
